class LVCollectionReviewsV58 extends HTMLElement {
  get isEnglish() { return this.dataset.locale === 'en'; }
  say(turkish, english) { return this.isEnglish ? english : turkish; }
  reviewTitle(review) { return this.isEnglish ? (review.title_en || review.title) : review.title; }
  reviewText(review) { return this.isEnglish ? (review.text_en || review.text) : review.text; }
  reviewResponse(review) { return this.isEnglish ? (review.merchant_response_en || review.merchant_response || review.merchantResponse) : (review.merchant_response || review.merchantResponse); }
  questionText(question) { return this.isEnglish ? (question.question_en || question.question) : question.question; }
  answerText(question) { return this.isEnglish ? (question.answer_en || question.answer) : question.answer; }

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.rail = this.querySelector('[data-lvcr-rail]');
    this.progress = this.querySelector('[data-lvcr-progress]');
    this.dialog = this.querySelector('[data-lvcr-dialog]');
    this.status = this.querySelector('[data-lvcr-status]');
    this.list = this.querySelector('[data-lvcr-list]');
    this.filters = this.querySelector('[data-lvcr-filters]');
    this.distribution = this.querySelector('[data-lvcr-distribution]');
    this.photoDialog = this.querySelector('[data-lvcr-photo-dialog]');
    this.photoImage = this.querySelector('[data-lvcr-photo-image]');
    this.photoCaption = this.querySelector('[data-lvcr-photo-caption]');
    this.questionsDialog = this.querySelector('[data-lvcr-questions-dialog]');
    this.questionsList = this.querySelector('[data-lvcr-questions-list]');
    this.logoUrl = this.dataset.logo || '';
    this.products = this.readProducts();
    this.reviews = [];
    this.activeFilter = 'all';
    this.activeSort = 'newest';
    this.railCards = [...(this.rail?.querySelectorAll('[data-lvcr-review-card]') || [])];
    this.querySelectorAll('[data-review-sort]').forEach((select) => select.addEventListener('change', (event) => {
      this.activeSort = event.target.value;
      this.applyReviewControls();
    }));
    this.querySelectorAll('[data-review-view]').forEach((button) => button.addEventListener('click', () => {
      this.activeFilter = button.dataset.reviewView;
      this.applyReviewControls();
    }));
    this.applyReviewControls();
    this.photoItems = [...this.querySelectorAll('[data-lvcr-gallery-photo]')].map((button) => ({
      src: button.dataset.src,
      reviewId: button.dataset.reviewId,
      name: button.dataset.reviewName,
      model: button.dataset.model,
      date: button.dataset.date
    }));

    this.querySelector('[data-lvcr-prev]')?.addEventListener('click', () => this.moveRail(-1));
    this.querySelector('[data-lvcr-next]')?.addEventListener('click', () => this.moveRail(1));
    this.querySelector('[data-lvcr-open]')?.addEventListener('click', () => this.openAll());
    this.querySelector('[data-lvcr-close]')?.addEventListener('click', () => this.dialog?.close());
    this.querySelector('[data-lvcr-photo-close]')?.addEventListener('click', () => this.photoDialog?.close());
    this.querySelector('[data-lvcr-photo-prev]')?.addEventListener('click', () => this.showPhoto(this.photoIndex - 1));
    this.querySelector('[data-lvcr-photo-next]')?.addEventListener('click', () => this.showPhoto(this.photoIndex + 1));
    this.querySelector('[data-lvcr-questions-open]')?.addEventListener('click', () => this.openQuestions());
    this.querySelector('[data-lvcr-questions-close]')?.addEventListener('click', () => this.questionsDialog?.close());
    this.querySelector('[data-lvcr-question-form]')?.addEventListener('submit', (event) => this.submitQuestion(event));
    this.rail?.addEventListener('scroll', () => this.queueProgress(), { passive: true });
    this.addEventListener('click', (event) => this.handleClick(event));

    [this.dialog, this.photoDialog, this.questionsDialog].forEach((dialog) => dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    }));
    this.photoDialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.showPhoto(this.photoIndex - 1);
      if (event.key === 'ArrowRight') this.showPhoto(this.photoIndex + 1);
    });

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        document.documentElement.classList.toggle('lv-review-area-visible', entry.isIntersecting);
      }, { threshold: 0.12 });
      this.visibilityObserver.observe(this);
    }
    this.updateProgress();
    this.prepareExpandableContent(this);
  }

  sortByDate(items, getDate) {
    return [...items].sort((a, b) => {
      const first = Date.parse(getDate(a)), second = Date.parse(getDate(b));
      if (!Number.isFinite(first)) return Number.isFinite(second) ? 1 : 0;
      if (!Number.isFinite(second)) return -1;
      return this.activeSort === 'newest' ? second - first : first - second;
    });
  }

  applyReviewControls() {
    this.querySelectorAll('[data-review-sort]').forEach((select) => { select.value = this.activeSort; });
    this.querySelectorAll('[data-review-view]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.reviewView === this.activeFilter)));
    if (this.rail) {
      this.sortByDate(this.railCards, (card) => card.dataset.reviewDate).forEach((card) => {
        card.hidden = this.activeFilter === 'photo' && !card.querySelector('[data-lvcr-open-photo]');
        this.rail.append(card);
      });
      this.rail.scrollLeft = 0;
      this.updateProgress();
    }
    if (this.loaded) this.renderReviews(this.activeFilter);
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    document.documentElement.classList.remove('lv-review-area-visible');
  }

  handleClick(event) {
    const galleryButton = event.target.closest('[data-lvcr-gallery-photo]');
    if (galleryButton) {
      const index = [...this.querySelectorAll('[data-lvcr-gallery-photo]')].indexOf(galleryButton);
      this.openPhoto(index);
      return;
    }
    const cardPhoto = event.target.closest('[data-lvcr-open-photo]');
    if (cardPhoto) {
      const reviewId = cardPhoto.dataset.lvcrOpenPhoto;
      const offset = Number(cardPhoto.dataset.photoIndex) || 0;
      const matches = this.photoItems.map((item, index) => ({ item, index })).filter(({ item }) => item.reviewId === reviewId);
      if (matches.length) this.openPhoto(matches[Math.min(offset, matches.length - 1)].index);
      else if (cardPhoto.dataset.src) {
        const index = this.photoItems.push({
          src: cardPhoto.dataset.src,
          reviewId,
          name: cardPhoto.dataset.reviewName,
          model: cardPhoto.dataset.model,
          date: cardPhoto.dataset.date
        }) - 1;
        this.openPhoto(index);
      }
      return;
    }
    const toggleButton = event.target.closest('[data-lvcr-toggle]');
    if (toggleButton) this.toggleExpandable(toggleButton);
  }

  readProducts() {
    try {
      const products = JSON.parse(this.querySelector('[data-lvcr-products]')?.textContent || '[]');
      if (!this.isEnglish) return products;
      return products.map((product) => ({
        ...product,
        reviews: (product.reviews || []).map((review) => {
          const translation = product.manual_translations?.[String(review.id)] || {};
          return {
            ...review,
            title_en: review.title_en || translation.title_en || null,
            text_en: review.text_en || translation.text_en || null,
            merchant_response_en: review.merchant_response_en || translation.merchant_response_en || null,
            manuallyTranslated: !review.text_en && Boolean(translation.text_en)
          };
        })
      }));
    }
    catch (_error) { return []; }
  }

  moveRail(direction) {
    if (!this.rail) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.rail.scrollBy({ left: direction * Math.max(240, this.rail.clientWidth * 0.88), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  queueProgress() {
    if (this.progressFrame) return;
    this.progressFrame = requestAnimationFrame(() => {
      this.progressFrame = null;
      this.updateProgress();
    });
  }

  updateProgress() {
    if (!this.rail || !this.progress) return;
    const cards = [...this.rail.querySelectorAll('.lvcr58__card')].filter((card) => !card.hidden);
    if (!cards.length) return;
    const center = this.rail.scrollLeft + this.rail.clientWidth / 2;
    let active = 0;
    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const currentCenter = cards[active].offsetLeft + cards[active].offsetWidth / 2;
      if (Math.abs(cardCenter - center) < Math.abs(currentCenter - center)) active = index;
    });
    this.progress.textContent = `${active + 1} / ${cards.length} ${this.say('deneyim', 'experiences')}`;
  }

  openAll(reviewId = '') {
    if (!this.dialog) return;
    if (!this.loaded && !this.loading) this.loadAll();
    this.dialog.showModal();
    if (reviewId) requestAnimationFrame(() => {
      const target = this.list?.querySelector(`[data-review-id="${CSS.escape(reviewId)}"]`);
      target?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      target?.focus({ preventScroll: true });
    });
  }

  loadAll() {
    this.loading = true;
    this.setStatus(this.say('Yorumlar hazırlanıyor…', 'Preparing reviews…'));
    this.reviews = this.products.flatMap((product) => Array.isArray(product.reviews)
      ? product.reviews.map((review) => ({ ...review, product })) : [])
      ;
    this.loading = false;
    this.loaded = this.reviews.length > 0;
    if (!this.reviews.length) {
      this.setStatus(this.say('Yorumlar şu anda görüntülenemiyor.', 'Reviews are unavailable right now.'));
      return;
    }
    this.renderDistribution();
    this.renderFilters();
    this.renderReviews(this.activeFilter);
  }

  dateValue(value) {
    const date = new Date(value || 0);
    return Number.isNaN(date.valueOf()) ? 0 : date.valueOf();
  }

  cleanReviewText(value) {
    const text = String(value || '').trim();
    const lines = text.split(/\r?\n/);
    const internalHeader = /(?:old_shopify_order|verified_purchase|customer_experience|shopify_order)/.test(lines[0] || '');
    return (internalHeader ? lines.slice(1).join('\n').trim() : text)
      .replace(/\b(?:old_shopify_order|verified_purchase|customer_experience|shopify_order)\b/g, '')
      .replace(/\s*·\s*·\s*/g, ' · ').trim();
  }

  setStatus(message = '') {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
  }

  renderDistribution() {
    if (!this.distribution) return;
    this.distribution.replaceChildren();
    const heading = document.createElement('p');
    heading.textContent = this.say('Puan dağılımı', 'Rating distribution');
    this.distribution.append(heading);
    for (let value = 5; value >= 1; value -= 1) {
      const count = this.reviews.filter((review) => Number(review.rating) === value).length;
      const item = document.createElement('div');
      item.className = 'lvcr58__distribution-item';
      item.setAttribute('aria-label', this.say(`${value} yıldızlı ${count} yorum`, `${count} reviews with ${value} stars`));
      const width = this.reviews.length ? (count / this.reviews.length) * 100 : 0;
      item.innerHTML = `<span>${value}★</span><i><b style="width:${width}%"></b></i><em>${count}</em>`;
      this.distribution.append(item);
    }
  }

  renderFilters() {
    if (!this.filters) return;
    this.filters.replaceChildren();
    this.filters.append(this.filterButton('all', this.say(`Tümü · ${this.reviews.length}`, `All · ${this.reviews.length}`)));
    const photoCount = this.reviews.filter((review) => Array.isArray(review.media) && review.media.length).length;
    this.filters.append(this.filterButton('photo', this.say(`Fotoğraflı · ${photoCount}`, `With photos · ${photoCount}`)));
  }

  filterButton(value, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lvcr58__filter';
    button.dataset.filter = value;
    button.setAttribute('aria-pressed', String(value === 'all'));
    button.textContent = label;
    button.addEventListener('click', () => this.setFilter(value));
    return button;
  }

  setFilter(value) {
    this.activeFilter = value;
    this.filters?.querySelectorAll('[data-filter]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.filter === value)));
    this.renderReviews(value);
  }

  renderReviews(filter) {
    if (!this.list) return;
    let reviews = this.sortByDate(this.reviews, (review) => review.date);
    if (filter === 'photo') reviews = reviews.filter((review) => Array.isArray(review.media) && review.media.length);
    this.list.replaceChildren(...reviews.map((review) => this.reviewCard(review)));
    this.setStatus(this.say(`${reviews.length} müşteri deneyimi gösteriliyor.`, `Showing ${reviews.length} customer experiences.`));
    this.prepareExpandableContent(this.list);
  }

  reviewCard(review) {
    const article = document.createElement('article');
    article.className = 'lvcr58__full-card';
    article.dataset.reviewId = review.id;
    article.tabIndex = -1;
    const ratingValue = Math.max(1, Math.min(5, Number(review.rating) || 1));
    const date = this.formatDate(review.date);
    article.innerHTML = `<div class="lvcr58__full-card-top"><div><strong class="lvcr58__name"></strong><span class="lvcr58__badge"></span></div><div class="lvcr58__rating"><span aria-label="${this.say(`5 üzerinden ${ratingValue} puan`, `${ratingValue} out of 5 stars`)}">${'★'.repeat(ratingValue)}${'☆'.repeat(5 - ratingValue)}</span>${date ? `<time class="lvcr58__full-date" datetime="${this.escape(review.date)}">${date}</time>` : ''}</div></div>`;
    article.querySelector('.lvcr58__name').textContent = review.name || this.say('VeloraWork müşterisi', 'VeloraWork customer');
    const badge = article.querySelector('.lvcr58__badge');
    badge.textContent = review.badge === 'verified_purchase' ? this.say('✓ Doğrulanmış alışveriş', '✓ Verified purchase') : this.say('Kayıtlı müşteri deneyimi', 'Recorded customer experience');
    if (review.badge === 'customer_experience') badge.classList.add('lvcr58__badge--experience');
    const reviewTitle = this.reviewTitle(review);
    if (reviewTitle) {
      const title = document.createElement('h3'); title.textContent = reviewTitle; article.append(title);
    }
    const text = document.createElement('p');
    text.className = 'lvcr58__full-card-text'; text.id = `lvcr58-dialog-review-${review.id}`; text.dataset.lvcrExpandable = ''; text.textContent = this.cleanReviewText(this.reviewText(review)); article.append(text, this.expandButton(text, this.say('Yorumun tamamını oku', 'Read full review'), this.say('Yorumu kısalt', 'Show less')));
    if (this.isEnglish && review.text_en) {
      const note = document.createElement('p'); note.className = 'lvcr58__translation-note'; note.textContent = review.manuallyTranslated ? 'Customer in Türkiye · Translated from Turkish' : 'Customer in Türkiye · Automatically translated from Turkish'; article.append(note);
    }
    const context = this.contextBadges(review);
    if (context) article.append(context);
    if (Array.isArray(review.media) && review.media.length) article.append(this.mediaRow(review));
    const product = document.createElement('a');
    product.className = 'lvcr58__full-product'; product.href = review.product.url; product.textContent = this.say(`${review.product.title} modelini incele →`, `Explore ${review.product.title} →`); article.append(product);
    const merchantResponse = this.reviewResponse(review);
    if (merchantResponse) {
      const response = document.createElement('div'); response.className = 'lvcr58__merchant-response';
      const head = document.createElement('div'); head.className = 'lvcr58__brand-answer';
      const mark = this.brandMark();
      const label = document.createElement('strong'); label.textContent = this.say('VeloraWork yanıtı', 'VeloraWork reply');
      head.append(mark, label);
      const responseDate = review.merchant_response_date || review.merchantResponseDate;
      if (responseDate) { const time = document.createElement('time'); time.dateTime = responseDate; time.textContent = this.formatDate(responseDate); head.append(time); }
      const copy = document.createElement('p'); copy.id = `lvcr58-dialog-response-${review.id}`; copy.dataset.lvcrExpandable = ''; copy.textContent = merchantResponse;
      response.append(head, copy, this.expandButton(copy, this.say('VeloraWork yanıtının tamamını oku', 'Read full VeloraWork reply'), this.say('Yanıtı kısalt', 'Show less'))); article.append(response);
    }
    return article;
  }

  contextBadges(review) {
    const labels = this.isEnglish ? {
      usage_area: { office: 'Used at the office', cafe: 'Used at cafés', school: 'Used at university', design_studio: 'Used in a design studio', home: 'Used at home', other: 'Used in different settings' },
      usage_duration: { first_impression: 'First impression', one_month: 'Used for 1 month', three_months: 'Used for 3 months', six_months_plus: 'Used for 6+ months' },
      laptop_size: { '13_14': '13–14″ laptop', '15_15_6': '15–15.6″ laptop', '16': '16″ laptop', no_laptop: 'Does not carry a laptop' }
    } : {
      usage_area: { office: 'Ofiste kullanıyor', cafe: 'Kafede kullanıyor', school: 'Okulda kullanıyor', design_studio: 'Tasarım stüdyosunda', home: 'Evde kullanıyor', other: 'Farklı ortamlarda' },
      usage_duration: { first_impression: 'İlk izlenim', one_month: '1 aydır kullanıyor', three_months: '3 aydır kullanıyor', six_months_plus: '6+ aydır kullanıyor' },
      laptop_size: { '13_14': '13–14″ laptop', '15_15_6': '15–15,6″ laptop', '16': '16″ laptop', no_laptop: 'Laptop taşımıyor' }
    };
    const values = [labels.usage_area[review.usage_area], labels.usage_duration[review.usage_duration], labels.laptop_size[review.laptop_size]].filter(Boolean);
    if (!values.length) return null;
    const row = document.createElement('div'); row.className = 'lv-review-context-v67'; row.setAttribute('aria-label', this.say('Müşterinin kullanım bilgileri', 'Customer usage details'));
    values.forEach((value) => { const badge = document.createElement('span'); badge.textContent = value; row.append(badge); });
    return row;
  }

  brandMark() {
    const mark = document.createElement('span');
    mark.className = 'lvcr58__brand-mark';
    if (!this.logoUrl) { mark.textContent = 'V'; return mark; }
    const image = document.createElement('img');
    image.src = this.logoUrl; image.alt = ''; image.width = 22; image.height = 22; image.loading = 'lazy'; image.decoding = 'async';
    mark.append(image);
    return mark;
  }

  mediaRow(review) {
    const media = document.createElement('div'); media.className = 'lvcr58__full-media';
    review.media.forEach((item, index) => {
      const previewSrc = typeof item === 'string' ? item : (item.preview_url || item.url);
      const fullSrc = typeof item === 'string' ? item : (item.url || item.preview_url);
      if (!previewSrc || !fullSrc) return;
      let photoIndex = this.photoItems.findIndex((photo) => photo.src === fullSrc);
      if (photoIndex < 0) photoIndex = this.photoItems.push({ src: fullSrc, reviewId: review.id, name: review.name, model: review.product.title, date: review.date }) - 1;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'lvcr58__full-media-button';
      button.setAttribute('aria-label', this.say(`Müşteri fotoğrafı ${index + 1} büyük görüntüle`, `Open customer photo ${index + 1}`));
      button.addEventListener('click', () => this.openPhoto(photoIndex));
      const image = document.createElement('img'); image.src = previewSrc; image.alt = this.say(`${review.product.title} müşteri fotoğrafı ${index + 1}`, `${review.product.title} customer photo ${index + 1}`); image.loading = 'lazy'; image.decoding = 'async'; image.width = 92; image.height = 92;
      button.append(image); media.append(button);
    });
    return media;
  }

  openPhoto(index) {
    if (!this.photoDialog || !this.photoItems.length) return;
    this.showPhoto(index);
    this.photoDialog.showModal();
  }

  expandButton(target, expandLabel, collapseLabel) {
    const button = document.createElement('button');
    button.type = 'button'; button.className = 'lvcr58__read'; button.dataset.lvcrToggle = '';
    button.setAttribute('aria-controls', target.id); button.setAttribute('aria-expanded', 'false');
    button.dataset.expandLabel = expandLabel; button.dataset.collapseLabel = collapseLabel;
    button.textContent = expandLabel; button.hidden = true;
    return button;
  }

  prepareExpandableContent(scope) {
    requestAnimationFrame(() => {
      scope?.querySelectorAll('[data-lvcr-toggle]').forEach((button) => {
        const target = document.getElementById(button.getAttribute('aria-controls'));
        if (!target) return;
        button.hidden = target.scrollHeight <= target.clientHeight + 2;
      });
    });
  }

  toggleExpandable(button) {
    const target = document.getElementById(button.getAttribute('aria-controls'));
    if (!target) return;
    const expanded = button.getAttribute('aria-expanded') !== 'true';
    target.classList.toggle('is-expanded', expanded);
    button.setAttribute('aria-expanded', String(expanded));
    button.textContent = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel;
  }

  findReview(reviewId) {
    for (const product of this.products) {
      const review = Array.isArray(product.reviews)
        ? product.reviews.find((item) => String(item.id) === String(reviewId))
        : null;
      if (review) return { ...review, product };
    }
    return this.reviews.find((review) => String(review.id) === String(reviewId));
  }

  openQuestions() {
    if (!this.questionsDialog || !this.questionsList) return;
    this.questionsList.replaceChildren();
    this.products.flatMap((product) => Array.isArray(product.questions)
      ? product.questions.map((question) => ({ ...question, product })) : [])
      .sort((a, b) => this.dateValue(b.answer_date || b.date) - this.dateValue(a.answer_date || a.date))
      .forEach((question) => {
        const details = document.createElement('details'); details.className = 'lvcr58__question-item';
        const summary = document.createElement('summary');
        const copy = document.createElement('span'); copy.textContent = this.questionText(question);
        const icon = document.createElement('b'); icon.ariaHidden = 'true'; icon.textContent = '＋'; summary.append(copy, icon);
        const answer = document.createElement('div'); answer.className = 'lvcr58__question-answer';
        const head = document.createElement('div'); head.className = 'lvcr58__brand-answer';
        const mark = this.brandMark();
        const label = document.createElement('strong'); label.textContent = this.say('VeloraWork yanıtı', 'VeloraWork reply'); head.append(mark, label);
        if (question.answer_date) { const time = document.createElement('time'); time.dateTime = question.answer_date; time.textContent = this.formatDate(question.answer_date); head.append(time); }
        const text = document.createElement('p'); text.textContent = this.answerText(question);
        const product = document.createElement('a'); product.href = question.product.url; product.textContent = this.say(`${question.product.title} modelini incele →`, `Explore ${question.product.title} →`);
        answer.append(head, text, product); details.append(summary, answer); this.questionsList.append(details);
      });
    this.questionsDialog.showModal();
  }

  async submitQuestion(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const status = form.querySelector('[data-lvcr-question-status]');
    const submit = form.querySelector('[type="submit"]');
    const productId = form.elements.productId.value;
    status.textContent = this.say('Sorun güvenle gönderiliyor…', 'Sending your question securely…'); submit.disabled = true;
    try {
      const response = await fetch(`/apps/velora-reviews/api/public/products/${encodeURIComponent(productId)}/questions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name: form.elements.name.value, email: form.elements.email.value, question: form.elements.question.value, website: form.elements.website.value })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || this.say('Soru gönderilemedi.', 'Your question could not be sent.'));
      form.reset(); status.textContent = this.say('Sorun alındı. VeloraWork yanıtladıktan sonra burada yayımlanacak.', 'We received your question. It will appear here after VeloraWork replies.');
    } catch (error) { status.textContent = error.message || this.say('Soru gönderilemedi. Lütfen yeniden dene.', 'Your question could not be sent. Please try again.'); }
    finally { submit.disabled = false; }
  }

  showPhoto(index) {
    if (!this.photoItems.length) return;
    this.photoIndex = (index + this.photoItems.length) % this.photoItems.length;
    const item = this.photoItems[this.photoIndex];
    this.photoImage.src = item.src;
    this.photoImage.alt = this.say(`${item.model} müşteri fotoğrafı`, `${item.model} customer photo`);
    const review = this.findReview(item.reviewId);
    const meta = document.createElement('div'); meta.className = 'lvcr58__photo-meta';
    const name = document.createElement('strong'); name.textContent = item.name || review?.name || this.say('VeloraWork müşterisi', 'VeloraWork customer');
    const details = document.createElement('span');
    details.textContent = [item.model || review?.product?.title, item.date ? this.formatDate(item.date) : this.formatDate(review?.date)].filter(Boolean).join(' · ');
    meta.append(name, details);
    const quote = document.createElement('p'); quote.textContent = this.cleanReviewText(review ? this.reviewText(review) : '');
    this.photoCaption.replaceChildren(meta);
    if (quote.textContent) this.photoCaption.append(quote);
    const multiple = this.photoItems.length > 1;
    this.querySelector('[data-lvcr-photo-prev]').hidden = !multiple;
    this.querySelector('[data-lvcr-photo-next]').hidden = !multiple;
  }

  formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) return '';
    return new Intl.DateTimeFormat(this.isEnglish ? 'en-US' : 'tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }

  escape(value) {
    return String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]);
  }
}

if (!customElements.get('lv-collection-reviews-v58')) customElements.define('lv-collection-reviews-v58', LVCollectionReviewsV58);
